import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  TablePagination,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  CalendarMonth as CalendarIcon,
  Business as CompanyIcon,
} from "@mui/icons-material";
import { AdvancedMdContext } from "../../../../../context/AdvancedMD/AdvancedMdContext";
import { formatDate, formatDateTime } from "../../../../../utils/formatters/date";

// Group labs by id + profileId
const groupLabs = (labs) => {
  const groups = {};

  labs.forEach((lab) => {
    // Create a unique key based on id + profileId + obsDateTime + resultCode
    const key = `${lab.id || ""}_${lab.profileId || ""}_${lab.obsDateTime || ""}_${lab.resultCode || ""}`;

    if (!groups[key]) {
      groups[key] = {
        id: lab.id,
        profileId: lab.profileId,
        companyName: lab.companyName,
        obsDateTime: lab.obsDateTime,
        specDateTime: lab.specDateTime,
        signBy: lab.signBy,
        signAt: lab.signAt,
        resultCode: lab.resultCode,
        resultDesc: lab.resultDesc,
        resultUnits: lab.resultUnits,
        resultRange: lab.resultRange,
        resultStatus: lab.resultStatus,
        results: [],
      };
    }

    // Add the result value to the group
    if (lab.resultValue) {
      groups[key].results.push({
        value: lab.resultValue,
        units: lab.resultUnits,
        range: lab.resultRange,
        status: lab.resultStatus,
      });
    }
  });

  // Convert to array and sort by date (newest first)
  return Object.values(groups).sort((a, b) => {
    const dateA = new Date(a.obsDateTime || 0);
    const dateB = new Date(b.obsDateTime || 0);
    return dateB - dateA;
  });
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function LabsPanel({ advancedMdPatientId, labs: labsProp, loading: loadingProp }) {
  const { labs: labsContext, labsLoading: labsLoadingContext, labsError, loadLabs } = useContext(AdvancedMdContext);

  const [expandedPanel, setExpandedPanel] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Use props if provided, otherwise use context
  const labs = labsProp !== undefined ? labsProp : labsContext;
  const labsLoading = loadingProp !== undefined ? loadingProp : labsLoadingContext;

  // Normalize response
  const labRows = useMemo(() => {
    const arr = labs?.data ?? labs;
    return Array.isArray(arr) ? arr : [];
  }, [labs]);

  // Group the labs
  const groupedLabs = useMemo(() => groupLabs(labRows), [labRows]);

  // Paginated labs - only render the current page
  const paginatedLabs = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return groupedLabs.slice(startIndex, endIndex);
  }, [groupedLabs, page, rowsPerPage]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedPanel(null); // Collapse any expanded panel when changing page
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedPanel(null);
  };

  // Load if needed
  useEffect(() => {
    if (labsProp !== undefined) return;
    if (!advancedMdPatientId) return;
    loadLabs(advancedMdPatientId);
  }, [advancedMdPatientId, loadLabs, labsProp]);

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ScienceIcon color="primary" />
        <Box>
          <Typography variant="h6" fontWeight={900}>
            Lab Results
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {groupedLabs.length} lab panel(s) • {labRows.length} total result(s)
          </Typography>
        </Box>
      </Stack>

      {labsLoading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading lab results...</Typography>
        </Box>
      )}

      {!!labsError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {labsError?.message || String(labsError)}
        </Alert>
      )}

      {!labsLoading && !labsError && groupedLabs.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No lab results found for this patient.
        </Alert>
      )}

      {!labsLoading && !labsError && groupedLabs.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {paginatedLabs.map((group, idx) => {
            const panelId = `lab-panel-${idx}`;
            const isExpanded = expandedPanel === panelId;

            return (
              <Accordion
                key={panelId}
                expanded={isExpanded}
                onChange={handleAccordionChange(panelId)}
                sx={{
                  mb: 1,
                  "&:before": { display: "none" },
                  boxShadow: isExpanded ? 2 : 0,
                  border: "1px solid",
                  borderColor: isExpanded ? "primary.main" : "grey.300",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: isExpanded ? "primary.50" : "grey.50",
                    "&:hover": { bgcolor: "primary.50" },
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    sx={{ width: "100%", pr: 2 }}
                  >
                    {/* Lab Name/Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {group.resultDesc || group.resultCode || "Lab Result"}
                      </Typography>
                      {group.resultCode && group.resultDesc && (
                        <Typography variant="caption" color="text.secondary">
                          Code: {group.resultCode}
                        </Typography>
                      )}
                    </Box>

                    {/* Quick Info Chips */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        icon={<CalendarIcon sx={{ fontSize: 16 }} />}
                        label={formatDate(group.obsDateTime)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.75rem" }}
                      />
                      {group.companyName && (
                        <Chip
                          icon={<CompanyIcon sx={{ fontSize: 16 }} />}
                          label={group.companyName}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontSize: "0.75rem" }}
                        />
                      )}
                      <Chip
                        label={`${group.results.length} result(s)`}
                        size="small"
                        color="primary"
                        sx={{ fontSize: "0.75rem" }}
                      />
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  {/* Lab Metadata */}
                  <Box sx={{ p: 2, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "grey.200" }}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={3}
                      divider={<Divider orientation="vertical" flexItem />}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Observation Date
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateTime(group.obsDateTime)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Specimen Date
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateTime(group.specDateTime)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Signed By
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {group.signBy || "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Signed At
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateTime(group.signAt)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Lab Company
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {group.companyName || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Results Table */}
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Results
                    </Typography>

                    {group.results.length === 1 && !group.results[0].value?.includes("\n") ? (
                      // Single simple result - show inline
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body1">
                          {group.results[0].value}
                          {group.results[0].units && (
                            <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                              {group.results[0].units}
                            </Typography>
                          )}
                        </Typography>
                        {group.results[0].range && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Reference Range: {group.results[0].range}
                          </Typography>
                        )}
                      </Paper>
                    ) : (
                      // Multiple results or multiline - show as list
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          {group.results.map((result, rIdx) => (
                            <Box
                              key={rIdx}
                              sx={{
                                p: 1.5,
                                bgcolor: rIdx % 2 === 0 ? "grey.50" : "white",
                                borderRadius: 1,
                                borderLeft: 3,
                                borderLeftColor: "primary.main",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {result.value}
                              </Typography>
                              {(result.units || result.range) && (
                                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                  {result.units && (
                                    <Typography variant="caption" color="text.secondary">
                                      Units: {result.units}
                                    </Typography>
                                  )}
                                  {result.range && (
                                    <Typography variant="caption" color="text.secondary">
                                      Range: {result.range}
                                    </Typography>
                                  )}
                                </Stack>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Box>

                  {/* Additional Info */}
                  {(group.resultUnits || group.resultRange || group.resultStatus) && (
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Stack direction="row" spacing={2}>
                        {group.resultUnits && (
                          <Chip label={`Units: ${group.resultUnits}`} size="small" variant="outlined" />
                        )}
                        {group.resultRange && (
                          <Chip label={`Range: ${group.resultRange}`} size="small" variant="outlined" />
                        )}
                        {group.resultStatus && (
                          <Chip
                            label={group.resultStatus}
                            size="small"
                            color={
                              group.resultStatus.toLowerCase().includes("normal")
                                ? "success"
                                : group.resultStatus.toLowerCase().includes("abnormal")
                                ? "error"
                                : "default"
                            }
                          />
                        )}
                      </Stack>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Pagination */}
          <TablePagination
            component="div"
            count={groupedLabs.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Labs per page:"
            sx={{
              mt: 2,
              borderTop: "1px solid",
              borderColor: "grey.300",
              "& .MuiTablePagination-toolbar": {
                flexWrap: "wrap",
                justifyContent: "center",
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
}
